import { FC, useState, useEffect } from "react";
import { LoadingStatus } from "../../../../@types/enumsGlobal";
import { PricingRoundScaleDataProvider } from "../../../../Services/DataProviders/PricingRoundScaleDataProvider";
import { PricingConditionDataProvider } from "../../../../Services/DataProviders/PricingConditionDataProvider";
import { DefaultPricingModelCommandsPanel } from "../../../../components/commandsPanels/DefaultPricingModelCommandsPanel";
import { DefaultGrid } from "../../../../components/grids/default/defaultGrid";
import { ToolbarWrapper } from "../../../../components/plugins";
import useGridFilter from "../../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../../system/providers/plugin";
import { IPricingRoundScaleDTO, IPricingRoundScaleViewDTO } from "../../../../libs/coreapi-dto/dirs/pricingRoundScale";
import { IPricingConditionDTO, IPricingConditionViewDTO } from "../../../../libs/coreapi-dto/dirs/pricingCondition";
import PricingRoundScaleCreatorModal from "../selectorModal/PricingRoundScaleCreatorModal";



export interface ICopyGridProps extends ISelectorGridProps {
    gridId: string,
    plugin: IPluginSettings,
    baseGridFilter?: IGridFilter,
    id?: string
}

export interface IPricingRoundScaleDTOAndIdGlobal extends IPricingRoundScaleDTO {
    idPricingRoundScaleGlobal?: string
}

interface IModalStateProps {
    isOpen: boolean,
    modalData: IPricingRoundScaleDTOAndIdGlobal,
    variant: "create" | "edit" | "copy";
}

export const initialPricingRoundScale: IPricingRoundScaleDTOAndIdGlobal = {
    idPricingRoundSchemaGlobal: '',
    idPricingRoundScaleGlobal: '',
    conditionMethod: 'И',
    codePricingRoundMethod: 'И',
    codePricingRoundPrecision:'',
    priority: 0,
    roundScaling: false,
}

export const PricingRoundScaleGrid: FC<ICopyGridProps> = (props) => {

    const pluginCtx = usePluginContext();
    const appCtx = useAppContext()
    const pricingRoundScaleDP = new PricingRoundScaleDataProvider(appCtx.coreApiService)
    const pricingConditionDataProvider = new PricingConditionDataProvider(appCtx.coreApiService)

    const [data, setData] = useState<IPricingRoundScaleViewDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>();
    const [totalCount, setTotalCount] = useState(0);
    const [viewState, setViewState] = useState<GridStateType>("view");
    const [gridFilter, dispatchGridFilter] = useGridFilter(props.baseGridFilter);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [blockButton, setBlockButton] = useState<boolean>(true);

    const masterGridSelectedItem = pluginCtx.masterGrid.selectedItem?.idGlobal;

    useEffect(() => {
        getView();
    }, [masterGridSelectedItem, gridFilter])

    const [modalState, setModalState] = useState<IModalStateProps>({
        isOpen: false,
        modalData: initialPricingRoundScale,
        variant: 'create'
    });

    const getView = () => {
        if (masterGridSelectedItem) {
            setSelectedItem(undefined);
            pricingRoundScaleDP.getView({
                ...gridFilter,
                columnFilters: [
                    {
                        name: "idPricingRoundSchemaGlobal",
                        operator: 'Eq',
                        value: masterGridSelectedItem
                    }
                ]
            }, (data, totalCount) => {
                setData(data)
                setTotalCount(totalCount)
                data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData)
            })
        }
    }

    const getMaxPriority = () => {
        if (data.length === 0) return 1;

        let maxPriority = data[0]?.priority;
        for (let i = 1; i < data.length; i++) {
            if (data[i].priority > maxPriority) {
                maxPriority = data[i].priority;
            }
        }
        return maxPriority + 1;
    }

    const variantSaveData = (saveData: IPricingRoundScaleDTO, idPricingRoundScaleGlobal: string | undefined) => {
        
        if (idPricingRoundScaleGlobal) {
            pricingRoundScaleDP.update(idPricingRoundScaleGlobal as string, saveData, () => {
                setModalState(prev => ({ ...prev, isOpen: false }))
                getView();
            });
        } else {
            pricingRoundScaleDP.create(saveData, (idGlobal) => {
                setModalState(prev => ({ ...prev, isOpen: false }));
                getView();
            })
        }
    }

    return (
        <>
            <ToolbarWrapper>
                <DefaultPricingModelCommandsPanel
                    add={{
                        onClick: () => {
                            if (masterGridSelectedItem) {
                                setModalState({
                                    isOpen: true,
                                    modalData: { ...initialPricingRoundScale, idPricingRoundSchemaGlobal: masterGridSelectedItem, priority: getMaxPriority() }, variant: 'create'
                                });
                            }
                        },
                    }}
                    edit={{
                        onClick: () => {
                            if (selectedItem && blockButton) {
                                setBlockButton(false);
                                pricingRoundScaleDP.getById(selectedItem.idGlobal, async (entity) => {
                                    setModalState({ isOpen: true, modalData: { ...entity, idPricingRoundScaleGlobal: selectedItem.idGlobal }, variant: 'edit' });
                                    setBlockButton(true);
                                })
                            }
                        },
                        disabled: !selectedItem
                    }}
                    copy={{
                        onClick: async () => {
                            if (selectedItem && blockButton) {
                                setBlockButton(false);

                                // 1) Получаем копию документа
                                const entityCopy = await new Promise<IPricingRoundScaleDTO>((resolve) => {
                                    pricingRoundScaleDP.getById(selectedItem.idGlobal, (entity) => resolve(entity));
                                });
                                // Опечатка при копировании
                                const copyEntity = { ...entityCopy, idPricingRoundScaleGlobal: '', priority: getMaxPriority() };


                                //2) Получаем все условия вью.
                                const viewDataPricing = await new Promise<IPricingConditionViewDTO[]>((resolve) => {
                                    pricingConditionDataProvider.getView({
                                        ...gridFilter, columnFilters: [{
                                            name: 'idPricingRoundScaleGlobal',
                                            operator: 'Eq',
                                            value: selectedItem.idGlobal
                                        }]
                                    }, (data) => resolve(data));
                                });


                                if (viewDataPricing.length > 0) {
                                    //3 если есть условия то создаем новую позицию.

                                    const createdIdPricingRoundScaleGlobal = await new Promise<string>((resolve) => {
                                        pricingRoundScaleDP.create(copyEntity, (idPricingRoundScaleGlobalRequest) => {
                                            resolve(idPricingRoundScaleGlobalRequest);
                                        })
                                    })

                                    //4 Получаем все условия по вьюхе и добавляем новый idScaleGlobal
                                    const promisesCopyConditions = viewDataPricing.map((elemGet) => {
                                        return new Promise<IPricingConditionDTO>((resolve, reject) => {

                                            try {
                                                pricingConditionDataProvider.getById(elemGet.idGlobal, (condition) => {
                                                    resolve({
                                                        compareTypeEnum: condition.compareTypeEnum,
                                                        paramValue: condition.paramValue,
                                                        paramValueGlobal: '00000000-0000-0000-0000-000000000000',
                                                        paramValueChar: condition.paramValueChar,
                                                        paramValueRef: condition.paramValueRef,
                                                        idPricingConditionParamGlobal: condition.idPricingConditionParamGlobal,
                                                        idPricingAdpriceScaleGlobal: condition.idPricingAdpriceScaleGlobal,
                                                        idPricingMinAdpriceSchemaGlobal: condition.idPricingMinAdpriceSchemaGlobal,
                                                        idPricingRoundScaleGlobal: createdIdPricingRoundScaleGlobal,
                                                        idPricingExtremeAdpriceSchemaGlobal: condition.idPricingExtremeAdpriceSchemaGlobal,
                                                        idPricingPriceCalcSchemaGlobal: condition.idPricingPriceCalcSchemaGlobal,
                                                        ids: condition.ids
                                                    });

                                                })
                                            } catch (error) {
                                                reject(error)
                                            }

                                        });
                                    });

                                    try {
                                        const copyConditions = await Promise.all(promisesCopyConditions);
                                        //5 Создаем новую копию условий с новым ScaleIdGlobal
                                        const promisesCreateConditions = copyConditions.map((newCondition) => {
                                            // Проверка объекта на свойство ids, если данный объект не содержит ids значит будет создавать без ids
                                            const { ids, ...newPricingConditionData } = newCondition;
                                            const checkCondition = newCondition.ids === null || newCondition.ids.length === 0 ? newPricingConditionData : newCondition

                                            return new Promise<string>((resolve) => {
                                                pricingConditionDataProvider.create(checkCondition as any, (idGlobal) => {
                                                    resolve(idGlobal);
                                                })

                                            });
                                        });
                                        const createConditions = await Promise.all(promisesCreateConditions);

                                        if (createConditions) {
                                            setBlockButton(true);
                                            setModalState({
                                                isOpen: true,
                                                modalData: {
                                                    ...copyEntity,
                                                    idPricingRoundScaleGlobal: createdIdPricingRoundScaleGlobal
                                                },
                                                variant: 'copy'
                                            });
                                        }

                                    } catch (error) {
                                        throw new Error('Ошибка при генерации условий');
                                    }


                                } else {
                                    setBlockButton(true);
                                    setModalState({
                                        isOpen: true,
                                        modalData: {
                                            ...copyEntity,
                                            idPricingRoundScaleGlobal: ''
                                        },
                                        variant: 'copy'
                                    });
                                }
                            }
                        },
                        disabled: !selectedItem
                    }}
                    delete={{
                        onClick: () => {
                            if (selectedItem && blockButton) {
                                setBlockButton(false);
                                pricingRoundScaleDP.markDelete(selectedItem.idGlobal, () => {
                                    setBlockButton(true);
                                    getView();
                                });
                            }
                        },
                        disabled: !selectedItem
                    }}
                    refresh={{
                        onClick: () => {
                            getView();
                        }
                    }}
                    permission={props.plugin.permission}
                />
            </ToolbarWrapper>
            {
                <DefaultGrid
                    gridId={props.gridId}
                    data={data}
                    loadingStatus={loadingStatus}
                    setLoadingStatus={setLoadingStatus}
                    singleDirectory
                    totalCount={totalCount}
                    filter={gridFilter}
                    dataProvider={pricingRoundScaleDP}
                    plugin={props.plugin}
                    selectedItem={selectedItem}
                    onDoubleClick={(row) => props.onDoubleClick?.(row)}
                    onSelect={(row) => {
                        setSelectedItem(row ? row : undefined)
                        props.onSelect?.(row)
                    }}
                    onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                    onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                    onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                    onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                    onEnter={() => setViewState("edit")}
                />
            }
            {
                modalState.isOpen &&
                <PricingRoundScaleCreatorModal
                    viewDataForValidatePriority={data}
                    data={modalState.modalData}
                    variant={modalState.variant}
                    save={(saveData, idPricingRoundScaleGlobal) => {

                        if (modalState.variant === 'edit') {
                            variantSaveData(saveData, selectedItem?.idGlobal)
                        }
                        else if (modalState.variant === 'copy') {
                            variantSaveData(saveData, idPricingRoundScaleGlobal)
                        }
                        else if (modalState.variant === 'create') {
                            variantSaveData(saveData, idPricingRoundScaleGlobal)
                        }
                    }}
                    cancel={() => {
                        if (modalState.variant === 'create' || modalState.variant === 'copy') {
                            getView();
                        }
                        setModalState(prev => ({ ...prev, isOpen: false }))
                    }}
                />
            }
        </>
    )
}